import React, { useState, useRef } from "react";

// Import necessary Ant Design components and icons
import { EditOutlined, PlusOutlined } from "@ant-design/icons";


import "../../../styles/bulletinAdmin/components/bulletinBlock.scss";

// Import all block components

import ImageBlock from "../blocks/ImageBlock";
import VideoBlock from "../blocks/VideoBlock";
import FileBlock from "../blocks/FileBlock";
import TextBlock from "../blocks/TextBlock";

// Import block menu
import BlockInsertMenu from "../EbulletinBlockMenu";
import ActionMenu from "../EbulletinActionMenu";


export default function BlockCard({
  block,
  sectionIdx,
  blockIdx,
  sections,
  setSections,
  isEditing,
  onClick,
  moveBlock,
  index,
  totalItems = 0,
  handleInsertBlock,
  handleDeleteBlock,
}) {
  // Define a ref for the block element and state for drag-and-drop.
  const ref = useRef(null);
  // State to manage the drag-and-drop state and toolbar visibility
  // State to manage visibility of the floating toolbar
  const [toolbarVisible, setToolbarVisible] = useState(false);

  // Block drag-and-drop is intentionally disabled.

  // Handle two-column layout creation
  const handleTwoColumn = () => {
    const updated = [...sections];
    const existingBlock = updated[sectionIdx].blocks[blockIdx];

    updated[sectionIdx].blocks[blockIdx] = {
      id: existingBlock.id,
      layout: "two-column",
      type: "two-column",
      leftBlock: {
        ...existingBlock,
        id: `${existingBlock.id}-left`,
        type: "text",
        content: "",
        style: existingBlock.style || {},
      },
      rightBlock: {
        id: `${existingBlock.id}-right`,
        type: "text",
        content: "",
        url: "",
        fileName: "",
        fileSize: 0,
        style: {},
      },
    };
    setSections(updated);
  };

  // Handle three-column layout creation
  const handleThreeColumn = () => {
    const updated = [...sections];
    const existingBlock = updated[sectionIdx].blocks[blockIdx];

    updated[sectionIdx].blocks[blockIdx] = {
      id: existingBlock.id,
      layout: "three-column",
      type: "three-column",
      leftBlock: {
        ...existingBlock,
        id: `${existingBlock.id}-left`,
        type: "text",
        content: "",
        style: existingBlock.style || {},
      },
      centerBlock: {
        id: `${existingBlock.id}-center`,
        type: "text",
        content: "",
        url: "",
        fileName: "",
        fileSize: 0,
        style: {},
      },
      rightBlock: {
        id: `${existingBlock.id}-right`,
        type: "text",
        content: "",
        url: "",
        fileName: "",
        fileSize: 0,
        style: {},
      },
    };
    setSections(updated);
  };

  // Handle four-column layout creation
  const handleFourColumn = () => {
    const updated = [...sections];
    const existingBlock = updated[sectionIdx].blocks[blockIdx];

    updated[sectionIdx].blocks[blockIdx] = {
      id: existingBlock.id,
      layout: "four-column",
      type: "four-column",
      leftBlock: {
        ...existingBlock,
        id: `${existingBlock.id}-left`,
        type: "text",
        content: "",
        style: existingBlock.style || {},
      },
      centerLeftBlock: {
        id: `${existingBlock.id}-center-left`,
        type: "text",
        content: "",
        url: "",
        fileName: "",
        fileSize: 0,
        style: {},
      },
      centerRightBlock: {
        id: `${existingBlock.id}-center-right`,
        type: "text",
        content: "",
        url: "",
        fileName: "",
        fileSize: 0,
        style: {},
      },
      rightBlock: {
        id: `${existingBlock.id}-right`,
        type: "text",
        content: "",
        url: "",
        fileName: "",
        fileSize: 0,
        style: {},
      },
    };
    setSections(updated);
  };

  // This function updates properties of a block, including media-specific properties
  // and ensures backward compatibility with text blocks.
  const updateBlockProperty = (prop, value, column = null) => {
    const updated = [...sections];
    let targetBlock;

    if (column) {
      targetBlock = updated[sectionIdx].blocks[blockIdx][`${column}Block`];
    } else {
      targetBlock = updated[sectionIdx].blocks[blockIdx];
    }

    if (targetBlock) {
      targetBlock[prop] = value;

      // For media blocks, ensure we maintain all necessary properties
      if (["image", "video", "file"].includes(targetBlock.type)) {
        // Initialize media-specific properties if they don't exist
        if (!targetBlock.hasOwnProperty("url")) targetBlock.url = "";
        if (!targetBlock.hasOwnProperty("fileName")) targetBlock.fileName = "";
        if (!targetBlock.hasOwnProperty("fileSize")) targetBlock.fileSize = 0;
        if (!targetBlock.hasOwnProperty("mimeType")) targetBlock.mimeType = "";

        // Type-specific properties
        if (targetBlock.type === "image") {
          if (!targetBlock.hasOwnProperty("alt")) targetBlock.alt = "";
          if (!targetBlock.hasOwnProperty("caption")) targetBlock.caption = "";
        }
        if (targetBlock.type === "video") {
          if (!targetBlock.hasOwnProperty("title")) targetBlock.title = "";
          if (!targetBlock.hasOwnProperty("thumbnail"))
            targetBlock.thumbnail = "";
        }
      }

      setSections(updated);
    }
  };

  // Function to update the style of a block, either for the entire block or a specific column
  const updateBlockStyle = (newStyles, column = null) => {
    const updated = [...sections];
    let targetBlock;

    if (column) {
      targetBlock = updated[sectionIdx].blocks[blockIdx][`${column}Block`];
    } else {
      targetBlock = updated[sectionIdx].blocks[blockIdx];
    }

    if (targetBlock) {
      targetBlock.style = { ...(targetBlock.style || {}), ...newStyles };
      setSections(updated);
    }
  };

  // Universal content update function for backward compatibility with text blocks
  const updateColumnType = (newType, column) => {
    const updated = [...sections];
    const columnKey = `${column}Block`;
    const targetBlock = updated[sectionIdx].blocks[blockIdx][columnKey];

    if (targetBlock) {
      // Create a fresh block object based on type
      const baseBlock = {
        id: targetBlock.id,
        type: newType,
        style: targetBlock.style || {},
      };

      // Add type-specific properties
      if (["image", "video", "file"].includes(newType)) {
        baseBlock.url = "";
        baseBlock.fileName = "";
        baseBlock.fileSize = 0;
        baseBlock.mimeType = "";

        if (newType === "image") {
          baseBlock.alt = "";
          baseBlock.caption = "";
        }
        if (newType === "video") {
          baseBlock.title = "";
          baseBlock.thumbnail = "";
        }
      } else {
        // Text blocks
        baseBlock.content = "";
      }

      updated[sectionIdx].blocks[blockIdx][columnKey] = baseBlock;
      setSections(updated);
    }
  };

  // Function to render the block based on its type
  const renderBlockByType = (blockData, column = null) => {
    // If a column block is empty or has no type, show a placeholder
    if (!blockData || !blockData.type) {
      return (
        <div className="empty-block-placeholder">
          {isEditing ? "Click 'Change Type' to add content" : ""}
        </div>
      );
    }

    // Prepare common props for all block types
    const blockProps = {
      block: blockData,
      isEditing,
      updateBlockProperty: (prop, value) =>
        updateBlockProperty(prop, value, column),
      updateBlockStyle: (newStyles) => updateBlockStyle(newStyles, column),
      updateBlockContent: (content) =>
        updateBlockProperty("content", content, column),
      bulletinId: sections[sectionIdx]?.bulletin_id,
      sectionId: sections[sectionIdx]?.id,
    };

    switch (blockData.type) {
      case "image":
        return <ImageBlock {...blockProps} />;
      case "video":
        return <VideoBlock {...blockProps} />;
      case "file":
        return <FileBlock {...blockProps} />;
      case "text":
      default:
        return <TextBlock {...blockProps} />;
    }
  };

  // Reusable column renderer
  const renderColumn = (columnBlock, columnKey, placement = "bottomRight") => (
    <div className={`column ${columnKey}-column`}>
      {isEditing && (
        <div className="column-header">
          <BlockInsertMenu
            onSelect={(type) => updateColumnType(type, columnKey)}
            buttonProps={{ size: "small", type: "text", children: "Change Type" }}
            placement={placement}
            icon={<EditOutlined />}
          />
        </div>
      )}
      <div className="column-content">
        {renderBlockByType(columnBlock, columnKey)}
      </div>
    </div>
  );

  // Render the block card with drag and drop functionality
  const renderBlockContent = () => {
    if (block.layout === "two-column") {
      return (
        <div className="two-column-layout">
          {renderColumn(block.leftBlock, "left", "bottomRight")}
          {renderColumn(block.rightBlock, "right", "bottomLeft")}
        </div>
      );
    }

    if (block.layout === "three-column") {
      return (
        <div className="three-column-layout">
          {renderColumn(block.leftBlock, "left", "bottomRight")}
          {renderColumn(block.centerBlock, "center", "bottomRight")}
          {renderColumn(block.rightBlock, "right", "bottomLeft")}
        </div>
      );
    }

    if (block.layout === "four-column") {
      return (
        <div className="four-column-layout">
          {renderColumn(block.leftBlock, "left", "bottomRight")}
          {renderColumn(block.centerLeftBlock, "centerLeft", "bottomRight")}
          {renderColumn(block.centerRightBlock, "centerRight", "bottomLeft")}
          {renderColumn(block.rightBlock, "right", "bottomLeft")}
        </div>
      );
    }

    return renderBlockByType(block);
  };

  return (
    <div
      className={`block-container ${!isEditing ? "view-only" : ""}`}
      onMouseEnter={() => isEditing && setToolbarVisible(true)}
      onMouseLeave={() => setToolbarVisible(false)}
    >
      <div
        ref={ref}
        className={`block-wrapper ${isEditing ? "edit-mode" : ""} ${
          block.layout === "two-column" ? "two-column-wrapper" :
          block.layout === "three-column" ? "three-column-wrapper" :
          block.layout === "four-column" ? "four-column-wrapper" : ""
        }`}
        onClick={onClick}
      >
        {isEditing && (
          <div
            className={`floating-block-toolbar ${
              toolbarVisible ? "visible" : ""
            }`}
          >
            <div className="toolbar-group">
              <BlockInsertMenu
                currentIndex={index}
                handleInsertBlock={handleInsertBlock}
                icon={<PlusOutlined />}
              />
              <ActionMenu
                block={block}
                index={index}
                totalItems={totalItems}
                handleTwoColumn={handleTwoColumn}
                handleThreeColumn={handleThreeColumn}
                handleFourColumn={handleFourColumn}
                handleDeleteBlock={handleDeleteBlock}
              />
            </div>
          </div>
        )}
        <div className={`block-content ${!isEditing ? "view-only" : ""}`}>
          {renderBlockContent()}
        </div>
      </div>
    </div>
  );
}
